/**
 * Connectivity for livescore outbox:
 * - internetOnline: general net (external probes + navigator.onLine) → UI badge
 * - arenaOnline: same-origin Arena API reachable → gate for flush
 *
 * Flushing on "Google is up" while Arena is still down is how you get eternal
 * "Saving event" spinners. Gate flush on Arena; probe Arena hard when net returns.
 */

type Listener = (online: boolean) => void;

const CHECK_URLS = [
    'https://www.google.com/generate_204',
    'https://www.cloudflare.com/cdn-cgi/trace',
    'https://connectivitycheck.gstatic.com/generate_204',
];

const CHECK_INTERVAL_MS = 2500;
const FAILS_BEFORE_OFFLINE = 2;
const ARENA_PROBE_MS = 4000;

class ConnectivityMonitor {
    private internetOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    private arenaOnline = true;

    private failStreak = 0;

    private listeners = new Set<Listener>();

    private flushReadyListeners = new Set<() => void>();

    private wasCanFlush = true;

    private timer: ReturnType<typeof setInterval> | null = null;

    private started = false;

    /** UI badge: general internet. */
    public getIsOnline = (): boolean => this.internetOnline;

    /** Outbox may POST only when Arena answered recently. */
    public getCanFlush = (): boolean => this.internetOnline && this.arenaOnline;

    public subscribe = (listener: Listener): (() => void) => {
        this.listeners.add(listener);
        this.ensureStarted();
        listener(this.internetOnline);
        return (): void => {
            this.listeners.delete(listener);
        };
    };

    /** Fires when Arena becomes reachable while internet is up (flush may start). */
    public subscribeFlushReady = (listener: () => void): (() => void) => {
        this.flushReadyListeners.add(listener);
        this.ensureStarted();
        return (): void => {
            this.flushReadyListeners.delete(listener);
        };
    };

    /** Call after any successful Arena event HTTP. */
    public forceOnline = (): void => {
        this.failStreak = 0;
        this.arenaOnline = true;
        this.setInternetOnline(true);
        this.emitFlushReadyIfNeeded();
    };

    public ensureStarted = (): void => {
        if (this.started || typeof window === 'undefined') return;
        this.started = true;
        this.wasCanFlush = this.getCanFlush();
        window.addEventListener('online', this.onBrowserOnline);
        window.addEventListener('offline', this.onBrowserOffline);
        void this.runCheck();
        this.timer = setInterval(() => {
            void this.runCheck();
        }, CHECK_INTERVAL_MS);
    };

    /** Probe Arena now (e.g. before drain on reconnect). */
    public probeArenaNow = async (): Promise<boolean> => {
        const ok = await this.probeArena();
        this.arenaOnline = ok;
        this.emitFlushReadyIfNeeded();
        return ok;
    };

    private onBrowserOnline = (): void => {
        void this.runCheck();
    };

    private onBrowserOffline = (): void => {
        this.failStreak = FAILS_BEFORE_OFFLINE;
        this.arenaOnline = false;
        this.setInternetOnline(false);
        this.emitFlushReadyIfNeeded();
    };

    private setInternetOnline = (next: boolean): void => {
        if (this.internetOnline === next) return;
        this.internetOnline = next;
        this.listeners.forEach((l) => l(next));
        this.emitFlushReadyIfNeeded();
    };

    private emitFlushReadyIfNeeded = (): void => {
        const can = this.getCanFlush();
        if (can && !this.wasCanFlush) {
            this.flushReadyListeners.forEach((l) => l());
        }
        this.wasCanFlush = can;
    };

    private runCheck = async (): Promise<void> => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            this.failStreak = FAILS_BEFORE_OFFLINE;
            this.arenaOnline = false;
            this.setInternetOnline(false);
            return;
        }

        const netOk = await this.probeAny();
        if (!netOk) {
            this.failStreak += 1;
            if (this.failStreak >= FAILS_BEFORE_OFFLINE) {
                this.arenaOnline = false;
                this.setInternetOnline(false);
            }
            return;
        }

        this.failStreak = 0;
        // Net path works - confirm Arena before advertising a flush-ready online.
        this.arenaOnline = await this.probeArena();
        this.setInternetOnline(true);
        this.emitFlushReadyIfNeeded();
    };

    private probeAny = async (): Promise<boolean> => {
        for (const url of CHECK_URLS) {
            try {
                await fetch(url, {
                    method: 'GET',
                    mode: 'no-cors',
                    cache: 'no-store',
                });
                // Opaque success still means the network path worked.
                return true;
            } catch {
                // try next
            }
        }
        return false;
    };

    /**
     * Same-origin hit. Any HTTP status (401/404/500) means Arena is up;
     * only network error / abort means down.
     */
    private probeArena = async (): Promise<boolean> => {
        if (typeof window === 'undefined') return true;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), ARENA_PROBE_MS);
        try {
            await fetch('/api/Categories', {
                method: 'GET',
                cache: 'no-store',
                credentials: 'same-origin',
                signal: ctrl.signal,
            });
            return true;
        } catch {
            return false;
        } finally {
            clearTimeout(timer);
        }
    };
}

export const connectivityMonitor = new ConnectivityMonitor();
